import axios from 'axios';
import { useEffect, useState, useRef } from 'react';
import { baseURL, generateFacilityReport, getBasicFacilityReport } from '../../../../helpers/api';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  useTheme,
  TextField,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  FilterList,
  VisibilityOutlined,
  FileDownloadOutlined,
  PictureAsPdfOutlined,
  TableChartOutlined,
  BarChartOutlined,
} from '@mui/icons-material';
import { getYears } from '../../../../helpers/dateHelpers';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { reportTypes, userCats } from '../../../../helpers/appstate';

// Recharts components
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ReferenceLine,
} from 'recharts';
import ResultRecommendations from '../../result/result-view/ResultRecomendations';
import RecommendationList from '../../result/result-view/RecommendationList';
import AddRecommendationModal from '../../result/result-view/AddRecommendationModal';

export default function BasicFacilityReport({ props }) {
  const { userId, userCategory } = props;

  const theme = useTheme();
  const reportRef = useRef(null);
  const chartRef = useRef(null);

  const initFacility = { facilityId: '', facilityName: '' };
  const [facilityList, setFacilityList] = useState([]);
  const [facilityLoading, setFacilityLoading] = useState(false);
  const [facilitySelected, setFacilitySelected] = useState(initFacility);
  const [yearSelected, setYearSelected] = useState(new Date().getFullYear());
  const [quarterList, setQuarterList] = useState([]);
  const [quarterSelected, setQuarterSelected] = useState('');
  const [reportData, setReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);

  // console.log('reportData', reportData);

  // Tab State: 0 = Table, 1 = Charts
  const [activeTab, setActiveTab] = useState(0);

  // Status colors
  const STATUS_COLORS = {
    OK: '#2e7d32',
    RECHECK: '#ed6c02',
    EXCEEDED: '#d32f2f',
    UNKNOWN: '#757575',
  };

  useEffect(() => {
    fetchFacility();
  }, []);

  useEffect(() => {
    if (facilitySelected?.facilityId) {
      fetchQuarters();
    }
  }, [facilitySelected, yearSelected]);

  const fetchFacility = async () => {
    setFacilityLoading(true);
    try {
      const url = `${baseURL}/TelerikReports/GetFacilityList/${userId}`;
      const res = await axios.get(url);
      const { status, data } = res;
      if (status === 200) {
        setFacilityList(data);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
    } finally {
      setFacilityLoading(false);
    }
  };

  const fetchQuarters = async () => {
    try {
      const res = await generateFacilityReport(facilitySelected?.facilityId, yearSelected);
      const { data, status } = res?.result || {};
      if (status?.code === '00') {
        setQuarterList(data || []);
      }
    } catch (error) {
      console.error('Error fetching quarters:', error);
    }
  };

  const handlePreviewReport = async () => {
    setReportLoading(true);
    try {
      const res = await getBasicFacilityReport(
        facilitySelected?.facilityId,
        yearSelected,
        quarterSelected
      );

      // Extract array safely across various payload wrapper formats
      let rawData = [];
      if (Array.isArray(res)) rawData = res;
      else if (Array.isArray(res?.result?.data)) rawData = res.result.data;
      else if (Array.isArray(res?.data?.result?.data)) rawData = res.data.result.data;
      else if (Array.isArray(res?.data)) rawData = res.data;

      setReportData(rawData);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setReportLoading(false);
    }
  };

  // Helper to extract resultValue matching BOTH current samplePoint AND parameter
  const getResultValue = (point, paramId, paramMatch) => {
    if (!point?.results || !Array.isArray(point.results)) return '-';

    // Flatten all nested data items from point.results[].data
    const allDataItems = point.results.flatMap((resObj) => resObj?.data || []);

    const resMatch = allDataItems.find((r) => {
      const matchesPoint =
        r.samplePointId === point.samplePointId ||
        (r.samplePoint &&
          point.samplePointName &&
          r.samplePoint.trim().toLowerCase() === point.samplePointName.trim().toLowerCase());

      const matchesParam =
        r.parameterId === paramId ||
        (r.measurementParameterId &&
          paramMatch?.measurementParameterId &&
          String(r.measurementParameterId) === String(paramMatch.measurementParameterId));

      return matchesPoint && matchesParam;
    });

    if (
      resMatch &&
      resMatch.resultValue !== undefined &&
      resMatch.resultValue !== null &&
      resMatch.resultValue !== ''
    ) {
      return resMatch.resultValue;
    }

    return '-';
  };

  // Helper to get full raw result object for status/value evaluation
  const getResultObject = (point, paramId, paramMatch) => {
    if (!point?.results || !Array.isArray(point.results)) return null;
    const allDataItems = point.results.flatMap((resObj) => resObj?.data || []);
    return allDataItems.find((r) => {
      const matchesPoint =
        r.samplePointId === point.samplePointId ||
        (r.samplePoint &&
          point.samplePointName &&
          r.samplePoint.trim().toLowerCase() === point.samplePointName.trim().toLowerCase());

      const matchesParam =
        r.parameterId === paramId ||
        (r.measurementParameterId &&
          paramMatch?.measurementParameterId &&
          String(r.measurementParameterId) === String(paramMatch.measurementParameterId));

      return matchesPoint && matchesParam;
    });
  };

  // Helper to check if a value defaults/exceeds standards
  const checkIfExceeded = (point, paramId, paramMatch) => {
    const resObj = getResultObject(point, paramId, paramMatch);
    if (!resObj) return false;

    const rawVal = resObj.resultValue;
    const numVal =
      rawVal !== undefined && rawVal !== null && !isNaN(rawVal) ? Number(rawVal) : null;
    const nesreaLimit = parseFloat(paramMatch?.nesrea);
    const limitVal = !isNaN(nesreaLimit) ? nesreaLimit : null;
    const status = resObj.resultStatus || 'UNKNOWN';

    return (
      status === 'RECHECK' ||
      status === 'EXCEEDED' ||
      (limitVal !== null && numVal !== null && numVal > limitVal)
    );
  };

  // Helper to strip HTML tags like <sub> or <sup> for clean Excel exports & charts
  const cleanText = (str) => (str ? str.replace(/<[^>]*>?/gm, '') : '');

  // --- Export to Excel (Table View Only) ---
  const exportToExcel = () => {
    if (!reportData || reportData.length === 0) return;

    const workbook = XLSX.utils.book_new();
    const grouped = groupDataByMeasurement(reportData);

    Object.keys(grouped).forEach((groupName) => {
      const items = grouped[groupName];
      const sheetData = [];

      const paramMap = new Map();
      items.forEach((item) => {
        item.parameters?.forEach((p) => {
          if (!paramMap.has(p.parameterId)) {
            paramMap.set(p.parameterId, {
              name: cleanText(p.unit || p.parameterId),
              siUnit: cleanText(p.siUnit || ''),
              nesrea: p.nesrea || '-',
              world: p.world || '-',
            });
          }
        });
      });

      const paramsList = Array.from(paramMap.entries());

      const header1 = ['Sample Point', ...paramsList.map(([_, meta]) => meta.name)];
      const header2 = ['Units', ...paramsList.map(([_, meta]) => meta.siUnit)];
      const header3 = ['NESREA Limit', ...paramsList.map(([_, meta]) => meta.nesrea)];
      const header4 = ['World Bank Limit', ...paramsList.map(([_, meta]) => meta.world)];

      sheetData.push(header1, header2, header3, header4);

      items.forEach((point) => {
        const row = [point.samplePointName];
        paramsList.forEach(([paramId]) => {
          const match = point.parameters?.find((p) => p.parameterId === paramId);
          const value = getResultValue(point, paramId, match);
          row.push(value);
        });
        sheetData.push(row);
      });

      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      const safeSheetName = cleanText(groupName).substring(0, 30);
      XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);
    });

    XLSX.writeFile(
      workbook,
      `${facilitySelected.facilityName ||
        'Facility'}_Report_${yearSelected}_Q${quarterSelected}.xlsx`
    );
  };

  // --- Export Table to PDF ---
  const exportToPDF = async () => {
    if (!reportRef.current) return;
    const element = reportRef.current;

    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(
      `${facilitySelected.facilityName ||
        'Facility'}_Report_${yearSelected}_Q${quarterSelected}.pdf`
    );
  };

  // --- Export Chart View to PDF ---
  const exportChartToPDF = async () => {
    if (!chartRef.current) return;
    const element = chartRef.current;

    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(
      `${facilitySelected.facilityName ||
        'Facility'}_Charts_${yearSelected}_Q${quarterSelected}.pdf`
    );
  };

  // Helper function to group data by groupName
  const groupDataByMeasurement = (data) => {
    return data.reduce((acc, curr) => {
      const group = curr.groupName || 'General';
      if (!acc[group]) acc[group] = [];
      acc[group].push(curr);
      return acc;
    }, {});
  };

  // Extract Pie chart status analytics
  const getOverallStatusAnalytics = () => {
    const statusCounts = {};
    if (!reportData || reportData.length === 0) return [];

    reportData.forEach((point) => {
      const allResults = point.results?.flatMap((r) => r?.data || []) || [];
      allResults.forEach((res) => {
        const status = res.resultStatus || 'UNKNOWN';
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });
    });

    return Object.keys(statusCounts).map((status) => ({
      name: status,
      value: statusCounts[status],
    }));
  };

  // Render Graphical Charts View - Detailed Per Sample Point
  const renderChartsView = () => {
    if (reportLoading) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Preparing visual analytics...
          </Typography>
        </Stack>
      );
    }

    if (!reportData || reportData.length === 0) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%', color: 'text.secondary' }}
        >
          <Typography variant="h6">No Chart Data</Typography>
          <Typography variant="body2">Select controls on the right and click "Preview".</Typography>
        </Stack>
      );
    }

    const groupedData = groupDataByMeasurement(reportData);
    const overallStatusData = getOverallStatusAnalytics();

    return (
      <Box ref={chartRef} sx={{ p: 3, bgcolor: '#fff', minHeight: '100%' }}>
        <Typography variant="h5" fontWeight={700} align="center" gutterBottom color="primary">
          {facilitySelected?.facilityName || 'FACILITY ENVIRONMENTAL REPORT'} - ANALYTICS
        </Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Year: {yearSelected} | Quarter: {quarterSelected}
        </Typography>

        {/* Overall Status Distribution Header */}
        <Paper variant="outlined" sx={{ p: 2, mb: 4, bgcolor: 'grey.50' }}>
          <Grid2 container spacing={2} alignItems="center">
            <Grid2 size={{ xs: 12, md: 8 }}>
              <Typography variant="subtitle1" fontWeight={700} color="primary.dark">
                COMPLIANCE STATUS OVERVIEW
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Breakdown of all parameter test results measured across facility sample points.
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                {overallStatusData.map((st) => (
                  <Chip
                    key={st.name}
                    label={`${st.name}: ${st.value}`}
                    size="small"
                    sx={{
                      bgcolor: STATUS_COLORS[st.name] || '#757575',
                      color: 'white',
                      fontWeight: 700,
                    }}
                  />
                ))}
              </Stack>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Box sx={{ width: '100%', height: 160 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={overallStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={65}
                      paddingAngle={3}
                      dataKey="value"
                      nameKey="name"
                    >
                      {overallStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name] || '#000'} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Grid2>
          </Grid2>
        </Paper>

        {/* Individual Sample Point Charts by Group */}
        {Object.keys(groupedData).map((groupName) => {
          const points = groupedData[groupName];

          return (
            <Box key={groupName} sx={{ mb: 5 }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  textTransform: 'uppercase',
                  color: theme.palette.primary.dark,
                  borderLeft: 4,
                  borderColor: 'primary.main',
                  pl: 1,
                }}
              >
                {groupName}
              </Typography>

              <Grid2 container spacing={3}>
                {points.map((point) => {
                  const pointChartData = (point.parameters || [])
                    .map((param) => {
                      const resObj = getResultObject(point, param.parameterId, param);
                      const rawVal = resObj?.resultValue;
                      const numVal =
                        rawVal !== undefined && rawVal !== null && !isNaN(rawVal)
                          ? Number(rawVal)
                          : null;

                      const nesreaLimit = parseFloat(param.nesrea);
                      const limitVal = !isNaN(nesreaLimit) ? nesreaLimit : null;

                      const status = resObj?.resultStatus || 'UNKNOWN';

                      return {
                        parameter: cleanText(param.unit || param.parameterId),
                        value: numVal,
                        nesreaLimit: limitVal,
                        status: status,
                        siUnit: cleanText(param.siUnit || ''),
                      };
                    })
                    .filter((item) => item.value !== null);

                  return (
                    <Grid2 key={point.samplePointId} size={{ xs: 12, md: 6 }}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 1.5 }}
                        >
                          <Typography variant="subtitle2" fontWeight={700}>
                            {point.samplePointName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {pointChartData.length} Parameters
                          </Typography>
                        </Stack>

                        <Box sx={{ width: '100%', height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={pointChartData}
                              margin={{ top: 10, right: 20, left: -10, bottom: 40 }}
                            >
                              <XAxis
                                dataKey="parameter"
                                tick={{ fontSize: 10 }}
                                interval={0}
                                angle={-30}
                                textAnchor="end"
                              />
                              <YAxis tick={{ fontSize: 10 }} />
                              <Tooltip
                                formatter={(val, name, props) => [
                                  `${val} ${props.payload.siUnit}`,
                                  `Result (${props.payload.status})`,
                                ]}
                              />
                              <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                              <Bar dataKey="value" name="Result Value" radius={[3, 3, 0, 0]}>
                                {pointChartData.map((entry, index) => {
                                  const isExceeded =
                                    entry.status === 'RECHECK' ||
                                    entry.status === 'EXCEEDED' ||
                                    (entry.nesreaLimit !== null && entry.value > entry.nesreaLimit);

                                  return (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={isExceeded ? '#d32f2f' : '#2e7d32'}
                                    />
                                  );
                                })}
                              </Bar>

                              {pointChartData.some((d) => d.nesreaLimit !== null) && (
                                <ReferenceLine
                                  y={
                                    pointChartData.find((d) => d.nesreaLimit !== null)?.nesreaLimit
                                  }
                                  stroke="#ed6c02"
                                  strokeDasharray="3 3"
                                  label={{
                                    value: 'Standard Limit',
                                    fill: '#ed6c02',
                                    fontSize: 10,
                                    position: 'top',
                                  }}
                                />
                              )}
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>
                    </Grid2>
                  );
                })}
              </Grid2>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderReportTables = () => {
    if (reportLoading) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Generating report preview...
          </Typography>
        </Stack>
      );
    }

    if (!reportData || reportData.length === 0) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%', color: 'text.secondary' }}
        >
          <Typography variant="h6">No Report Data</Typography>
          <Typography variant="body2">Select controls on the right and click "Preview".</Typography>
        </Stack>
      );
    }

    const groupedData = groupDataByMeasurement(reportData);

    return (
      <Box ref={reportRef} sx={{ p: 3, bgcolor: '#fff', minHeight: '100%' }}>
        <Typography variant="h5" fontWeight={700} align="center" gutterBottom color="primary">
          {facilitySelected?.facilityName || 'FACILITY ENVIRONMENTAL REPORT'}
        </Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Year: {yearSelected} | Quarter: {quarterSelected}
        </Typography>

        {Object.keys(groupedData).map((groupName, idx) => {
          const points = groupedData[groupName];

          // Extract unique parameters across all sample points in this group
          const parameterMap = new Map();
          points.forEach((point) => {
            point.parameters?.forEach((p) => {
              if (!parameterMap.has(p.parameterId)) {
                parameterMap.set(p.parameterId, {
                  unitName: p.unit || p.parameterId,
                  siUnit: p.siUnit || '',
                  nesrea: p.nesrea || '-',
                  world: p.world || '-',
                });
              }
            });
          });

          const uniqueParams = Array.from(parameterMap.entries());

          return (
            <Box key={idx} sx={{ mb: 4 }}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  mb: 1,
                  textTransform: 'uppercase',
                  color: theme.palette.primary.dark,
                  borderLeft: 3,
                  borderColor: 'primary.main',
                  pl: 1,
                  fontSize: '0.85rem',
                }}
              >
                {groupName}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table
                  size="small"
                  sx={{
                    '& .MuiTableCell-root': {
                      fontSize: '0.725rem',
                      py: 0.5,
                      px: 0.75,
                      lineHeight: 1.2,
                    },
                  }}
                >
                  <TableHead sx={{ bgcolor: 'grey.100' }}>
                    {/* Header Row 1: Parameter Name */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 130 }}>Sample Point</TableCell>
                      {uniqueParams.map(([paramId, meta]) => (
                        <TableCell key={paramId} align="center" sx={{ fontWeight: 'bold' }}>
                          <span dangerouslySetInnerHTML={{ __html: meta.unitName }} />
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* Header Row 2: SI Units */}
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.675rem' }}
                      >
                        Unit
                      </TableCell>
                      {uniqueParams.map(([paramId, meta]) => (
                        <TableCell
                          key={paramId}
                          align="center"
                          sx={{ color: 'text.secondary', fontSize: '0.675rem' }}
                        >
                          <span dangerouslySetInnerHTML={{ __html: meta.siUnit }} />
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* Header Row 3: Limits */}
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.675rem' }}
                      >
                        NESREA / World Limit
                      </TableCell>
                      {uniqueParams.map(([paramId, meta]) => (
                        <TableCell
                          key={paramId}
                          align="center"
                          sx={{ fontSize: '0.675rem', color: 'warning.main', fontWeight: 600 }}
                        >
                          {meta.nesrea} / {meta.world}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {points.map((point) => (
                      <TableRow key={point.samplePointId} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{point.samplePointName}</TableCell>
                        {uniqueParams.map(([paramId]) => {
                          const paramMatch = point.parameters?.find(
                            (p) => p.parameterId === paramId
                          );
                          const value = getResultValue(point, paramId, paramMatch);
                          const isExceeded = checkIfExceeded(point, paramId, paramMatch);

                          return (
                            <TableCell
                              key={paramId}
                              align="center"
                              sx={{
                                color: isExceeded ? '#d32f2f' : 'inherit',
                                fontWeight: isExceeded ? 700 : 400,
                                bgcolor: isExceeded ? alpha('#d32f2f', 0.08) : 'transparent',
                              }}
                            >
                              {value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}

        {/* <ResultRecommendations
          facilityId={facilitySelected?.facilityId}
          quarter={quarterSelected}
          year={yearSelected}
        /> */}
        <RecommendationList
          facilityId={facilitySelected?.facilityId}
          quarter={quarterSelected}
          year={yearSelected}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ height: '90vh', bgcolor: 'background.default', overflow: 'hidden' }}>
      <Grid2 container sx={{ height: '100%' }} spacing={0}>
        {/* Main Preview Area */}
        <Grid2
          size={{ lg: 9, md: 8, sm: 12, xs: 12 }}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          {/* Top Header Bar with View Tabs and Export Options */}
          <Box
            sx={{
              px: 2,
              py: 0.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* View Mode Tabs */}
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              size="small"
              sx={{ minHeight: 38 }}
            >
              <Tab
                icon={<TableChartOutlined sx={{ fontSize: '1rem' }} />}
                iconPosition="start"
                label="Tabular"
                sx={{ textTransform: 'none', fontWeight: 600, minHeight: 38, fontSize: '0.8rem' }}
              />
              <Tab
                icon={<BarChartOutlined sx={{ fontSize: '1rem' }} />}
                iconPosition="start"
                label="Graphical"
                sx={{ textTransform: 'none', fontWeight: 600, minHeight: 38, fontSize: '0.8rem' }}
              />
            </Tabs>

            {/* Action Buttons */}
            {reportData.length > 0 && (
              <Stack direction="row" spacing={1}>
                {/* Table View Exports: Excel & PDF */}
                {activeTab === 0 && (
                  <>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      startIcon={<FileDownloadOutlined />}
                      onClick={exportToExcel}
                      sx={{
                        textTransform: 'none',
                        border: 1,
                        borderColor: 'divider',
                        fontSize: '0.8rem',
                      }}
                    >
                      Excel
                    </Button>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      startIcon={<PictureAsPdfOutlined />}
                      onClick={exportToPDF}
                      sx={{
                        textTransform: 'none',
                        border: 1,
                        borderColor: 'divider',
                        fontSize: '0.8rem',
                      }}
                    >
                      PDF
                    </Button>
                  </>
                )}

                {/* Chart View Export: PDF Only */}
                {activeTab === 1 && (
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    startIcon={<PictureAsPdfOutlined />}
                    onClick={exportChartToPDF}
                    sx={{
                      textTransform: 'none',
                      border: 1,
                      borderColor: 'divider',
                      fontSize: '0.8rem',
                    }}
                  >
                    PDF
                  </Button>
                )}
              </Stack>
            )}
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              borderRight: 1,
              borderColor: 'divider',
              overflowY: 'auto',
              bgcolor: '#f8f9fa',
            }}
          >
            {activeTab === 0 ? renderReportTables() : renderChartsView()}
          </Box>
        </Grid2>

        {/* Right Sidebar Controls */}
        <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }} sx={{ height: '100%', bgcolor: 'white' }}>
          <Stack sx={{ height: '85%' }}>
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterList color="primary" fontSize="small" />
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Preview Controls
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
              <Stack spacing={3}>
                <Autocomplete
                  fullWidth
                  options={facilityList}
                  getOptionLabel={(option) => option.facilityName || ''}
                  value={facilitySelected}
                  onChange={(_, value) => value && setFacilitySelected(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Facility"
                      size="small"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: facilityLoading && (
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                        ),
                      }}
                    />
                  )}
                />

                <FormControl fullWidth size="small">
                  <InputLabel>Year *</InputLabel>
                  <Select
                    label="Year *"
                    size="small"
                    value={yearSelected}
                    onChange={(e) => setYearSelected(e.target.value)}
                  >
                    {getYears().map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Quarter *</InputLabel>
                  <Select
                    label="Quarter *"
                    size="small"
                    value={quarterSelected}
                    onChange={(e) => setQuarterSelected(e.target.value)}
                  >
                    {quarterList?.map((y, index) => {
                      const isEdm = userCategory === userCats.EDM;
                      const isQaqc = y?.reportType === reportTypes.QAQC;

                      return (
                        <MenuItem key={index} value={y?.reportQuarter} disabled={isQaqc && !isEdm}>
                          Quarter {y?.reportQuarter}{' '}
                          {isQaqc && (
                            <Chip
                              label={y?.reportType}
                              size="small"
                              color={isEdm ? 'warning' : 'default'}
                              sx={{ ml: 1, fontSize: '0.8rem' }}
                            />
                          )}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            <Divider />

            <Box
              sx={{ p: 2, bgcolor: 'grey.50', display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={
                  reportLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <VisibilityOutlined />
                  )
                }
                disabled={reportLoading || !facilitySelected?.facilityId || !quarterSelected}
                onClick={handlePreviewReport}
                sx={{ py: 1.2, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
              >
                Preview
              </Button>
              {reportData?.length > 0 && (
                <AddRecommendationModal
                  facilityId={facilitySelected?.facilityId}
                  quarter={quarterSelected}
                  year={yearSelected}
                  onSuccess={() => handlePreviewReport()}
                />
              )}
            </Box>
          </Stack>
        </Grid2>
      </Grid2>
    </Box>
  );
}
